import { createClient } from '@/lib/supabase/client';
import { queryCache, buildSearchKey, memoFilter, clearMemoPrefix } from '@/lib/supabase/queryCache';
import { subscriptionBatcher } from '@/lib/supabase/subscriptionBatcher';
import { recordAudit } from './auditLogService';

/** Refresh the Supabase session to recover from clock-skew JWT errors */
async function refreshSessionIfNeeded(errorMessage: string): Promise<boolean> {
  if (!errorMessage.toLowerCase().includes('jwt') && !errorMessage.toLowerCase().includes('token')) {
    return false;
  }
  try {
    const supabase = createClient();
    const { error } = await supabase.auth.refreshSession();
    return !error;
  } catch {
    return false;
  }
}

export interface DutyAssignment {
  id: string;
  dutyCode: string;
  route: string;
  routeName: string;
  busNumber: string;
  driver1Id?: string;
  driver1Name: string;
  driver2Id?: string;
  driver2Name: string;
  conductorId?: string;
  conductorName: string;
  shift: 'Morning' | 'Afternoon' | 'Night';
  dutyDate: string;
  departureTime: string;
  returnTime: string;
  depot: string;
  status: 'Scheduled' | 'Active' | 'Completed' | 'Cancelled' | 'Delayed';
  notes: string;
  createdAt: string;
  updatedAt: string;
}

function mapRow(row: any): DutyAssignment {
  return {
    id: row.id,
    dutyCode: row.duty_code,
    route: row.route,
    routeName: row.route_name ?? '',
    busNumber: row.bus_number,
    driver1Id: row.driver1_id ?? undefined,
    driver1Name: row.driver1_name ?? '—',
    driver2Id: row.driver2_id ?? undefined,
    driver2Name: row.driver2_name ?? '—',
    conductorId: row.conductor_id ?? undefined,
    conductorName: row.conductor_name ?? '—',
    shift: row.shift,
    dutyDate: row.duty_date,
    departureTime: row.departure_time ?? '',
    returnTime: row.return_time ?? '',
    depot: row.depot ?? '',
    status: row.status,
    notes: row.notes ?? '',
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

const CACHE_KEY = 'duty_assignments';

export interface DutySearchParams {
  status?: string;
  shift?: string;
  depot?: string;
  route?: string;
  dutyDate?: string;
  search?: string; // matches duty_code, bus_number, driver1_name, conductor_name
}

export const dutyService = {
  async getAll(): Promise<DutyAssignment[]> {
    return queryCache.dedupe(CACHE_KEY, async () => {
      const supabase = createClient();
      const { data, error } = await supabase
        .from('duty_assignments')
        .select('id,duty_code,route,route_name,bus_number,driver1_id,driver1_name,driver2_id,driver2_name,conductor_id,conductor_name,shift,duty_date,departure_time,return_time,depot,status,notes,created_at,updated_at')
        .order('duty_date', { ascending: false })
        .order('departure_time', { ascending: true });
      if (error) { console.error('dutyService.getAll:', error.message); return []; }
      let result = (data ?? []).map(mapRow);
      queryCache.set(CACHE_KEY, result);
      clearMemoPrefix('duty_search:');
      return result;
    });
  },

  /**
   * Server-side filtered query using indexed columns.
   * Results are cached per unique param combination.
   */
  async search(params: DutySearchParams): Promise<DutyAssignment[]> {
    const cacheKey = buildSearchKey('duty_search', params as Record<string, unknown>);
    const cached = queryCache.get<DutyAssignment[]>(cacheKey);
    if (cached) return cached;

    const supabase = createClient();
    let query = supabase
      .from('duty_assignments')
      .select('*')
      .order('duty_date', { ascending: false })
      .order('departure_time', { ascending: true });

    if (params.status)   query = query.eq('status', params.status);
    if (params.shift)    query = query.eq('shift', params.shift);
    if (params.depot)    query = query.eq('depot', params.depot);
    if (params.route)    query = query.eq('route', params.route);
    if (params.dutyDate) query = query.eq('duty_date', params.dutyDate);

    let { data, error } = await query;

    // Retry once after session refresh if JWT clock-skew error
    if (error && (error.message.toLowerCase().includes('jwt') || error.message.toLowerCase().includes('issued at future'))) {
      console.warn('dutyService.search: JWT clock-skew detected, refreshing session and retrying…');
      const refreshed = await refreshSessionIfNeeded(error.message);
      if (refreshed) {
        const retryResult = await supabase
          .from('duty_assignments')
          .select('*')
          .order('duty_date', { ascending: false })
          .order('departure_time', { ascending: true });
        data = retryResult.data;
        error = retryResult.error;
      }
    }

    if (error) { console.error('dutyService.search:', error.message); return []; }

    let result = (data ?? []).map(mapRow);

    // Client-side text search across non-indexed text fields
    if (params.search) {
      const term = params.search.toLowerCase();
      result = result.filter(
        (d) =>
          d.dutyCode.toLowerCase().includes(term) ||
          d.busNumber.toLowerCase().includes(term) ||
          d.driver1Name.toLowerCase().includes(term) ||
          d.conductorName.toLowerCase().includes(term) ||
          d.routeName.toLowerCase().includes(term)
      );
    }

    queryCache.set(cacheKey, result, 20_000); // 20 s TTL for search results
    return result;
  },

  /**
   * Client-side memoized filter over the full cached list.
   * Use when the full list is already loaded and you need instant UI filtering.
   */
  filterMemo(allDuties: DutyAssignment[], params: DutySearchParams): DutyAssignment[] {
    const memoKey = buildSearchKey('duty_memo', params as Record<string, unknown>);
    return memoFilter(memoKey, allDuties, (items) => {
      let result = items;
      if (params.status)   result = result.filter((d) => d.status === params.status);
      if (params.shift)    result = result.filter((d) => d.shift === params.shift);
      if (params.depot)    result = result.filter((d) => d.depot === params.depot);
      if (params.route)    result = result.filter((d) => d.route === params.route);
      if (params.dutyDate) result = result.filter((d) => d.dutyDate === params.dutyDate);
      if (params.search) {
        const term = params.search!.toLowerCase();
        result = result.filter(
          (d) =>
            d.dutyCode.toLowerCase().includes(term) ||
            d.busNumber.toLowerCase().includes(term) ||
            d.driver1Name.toLowerCase().includes(term) ||
            d.conductorName.toLowerCase().includes(term) ||
            d.routeName.toLowerCase().includes(term)
        );
      }
      return result;
    });
  },

  async create(duty: Omit<DutyAssignment, 'id' | 'createdAt' | 'updatedAt'>): Promise<DutyAssignment | null> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('duty_assignments')
      .insert({
        duty_code: duty.dutyCode,
        route: duty.route,
        route_name: duty.routeName,
        bus_number: duty.busNumber,
        driver1_id: duty.driver1Id || null,
        driver1_name: duty.driver1Name,
        driver2_id: duty.driver2Id || null,
        driver2_name: duty.driver2Name,
        conductor_id: duty.conductorId || null,
        conductor_name: duty.conductorName,
        shift: duty.shift,
        duty_date: duty.dutyDate,
        departure_time: duty.departureTime,
        return_time: duty.returnTime,
        depot: duty.depot,
        status: duty.status,
        notes: duty.notes,
      })
      .select()
      .single();
    if (error) { console.error('dutyService.create:', error.message); return null; }
    queryCache.invalidate(CACHE_KEY);
    queryCache.invalidatePrefix('duty_search:');
    clearMemoPrefix('duty_memo:');
    let result = mapRow(data);
    await recordAudit({
      action: 'duty_assigned',
      target_type: 'duty_assignment',
      target_id: result.id,
      target_name: `${result.routeName || result.route} ${result.shift} Shift`,
      change_details: {
        duty_code: result.dutyCode,
        driver: result.driver1Name,
        conductor: result.conductorName,
        route: result.routeName || result.route,
        shift: result.shift,
        date: result.dutyDate,
        bus: result.busNumber,
      },
    });
    return result;
  },

  async update(id: string, updates: Partial<DutyAssignment>): Promise<DutyAssignment | null> {
    const supabase = createClient();
    const payload: any = {};
    if (updates.status !== undefined) payload.status = updates.status;
    if (updates.driver1Name !== undefined) payload.driver1_name = updates.driver1Name;
    if (updates.conductorName !== undefined) payload.conductor_name = updates.conductorName;
    if (updates.notes !== undefined) payload.notes = updates.notes;

    const { data, error } = await supabase
      .from('duty_assignments')
      .update(payload)
      .eq('id', id)
      .select()
      .single();
    if (error) { console.error('dutyService.update:', error.message); return null; }
    queryCache.invalidate(CACHE_KEY);
    queryCache.invalidatePrefix('duty_search:');
    clearMemoPrefix('duty_memo:');
    let result = mapRow(data);
    await recordAudit({
      action: 'duty_updated',
      target_type: 'duty_assignment',
      target_id: result.id,
      target_name: `${result.routeName || result.route} — ${result.dutyCode}`,
      change_details: { ...payload, duty_code: result.dutyCode },
    });
    return result;
  },

  async delete(id: string): Promise<boolean> {
    const supabase = createClient();
    const { data: existing } = await supabase.from('duty_assignments').select('duty_code, route_name, route').eq('id', id).maybeSingle();
    const { error } = await supabase.from('duty_assignments').delete().eq('id', id);
    if (error) { console.error('dutyService.delete:', error.message); return false; }
    queryCache.invalidate(CACHE_KEY);
    queryCache.invalidatePrefix('duty_search:');
    clearMemoPrefix('duty_memo:');
    await recordAudit({
      action: 'duty_deleted',
      target_type: 'duty_assignment',
      target_id: id,
      target_name: existing?.duty_code ?? id,
      change_details: { route: existing?.route_name ?? existing?.route ?? '' },
    });
    return true;
  },

  subscribeToChanges(callback: (duties: DutyAssignment[]) => void) {
    const supabase = createClient();
    const channel = supabase
      .channel('duty_assignments_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'duty_assignments' }, () => {
        queryCache.invalidate(CACHE_KEY);
        queryCache.invalidatePrefix('duty_search:');
        clearMemoPrefix('duty_memo:');
        subscriptionBatcher.schedule(
          CACHE_KEY,
          () => dutyService.getAll(),
          callback
        );
      })
      .subscribe();
    return () => {
      subscriptionBatcher.cancel(CACHE_KEY);
      supabase.removeChannel(channel);
    };
  },
};
